import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  FC,
  useRef,
  ElementRef,
  Fragment,
} from 'react';

import cn from 'classnames';
import { TbDotsVertical } from 'react-icons/tb';
import { dayjs } from 'utils/providers/Timezone';
import { useFormikContext } from 'formik';
import { MdClose } from 'react-icons/md';
import { compact, debounce, isNil } from 'lodash';
import { Button, DeleteItemModal } from 'ui/components/common';
import { useDealsContext } from 'modules/account/sales/containers/Deals/Deals';
import { useDeleteDeal, useGetStages } from 'modules/account/sales/hooks';
import { Dropdown, NavAction } from 'ui/components/nav';
import {
  Autocomplete,
  DateField,
  Currency,
  SelectAvatar,
  Select,
  TagsField,
} from 'ui/components/form';
import {
  useGetAccountUsers,
  useGetCompanies,
  useGetCompany,
  useGetLeads,
} from 'modules/account/contact/hooks';
import { Avatar } from 'ui/components/img';
import {
  useGetSubscription,
  useGetViewDatas,
} from 'modules/account/settings/hooks';
import {
  DATE_FORMAT_V2,
  SIZE,
  ACTIONS,
  ACCESS_LEVEL,
  PERMISSION_KEYS,
} from 'utils/constants';
import { convertToAmount, usdFormatter, useAdmin } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import {
  Deal,
  Pipeline,
  Tag as ITag,
  User,
  VIEWDATA_TYPE,
  DATA_SOURCE,
  Data,
  DATA_TYPE,
  DATA_NUMBER_FORMAT,
} from 'utils/types';
import constants from 'utils/constants';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import type { GroupBase, OptionsOrGroups } from 'react-select';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { useTour } from '@reactour/tour';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import Tag from 'ui/components/common/Tag';
import { CustomFieldsModal } from 'modules/account/contact/components';
import BaseCustomFieldFormField from 'modules/account/contact/forms/BaseCustomFieldFormField';

import { DEAL_FORM } from './schema';
import { composerDealForm } from '../utils/tour-steps/how-to-manage-a-deal/mockData';
import { mockCompanyData } from '../utils/tour-steps/how-to-view-created-deals/mockData';

interface PROPS {
  mode: 'modal' | 'popover';
  isLoading?: boolean;
  deal: Deal;
}

const EditDealForm: FC<PROPS> = ({
  mode = 'popover',
  isLoading = false,
  deal,
}) => {
  const canViewLeadsAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.LEADS,
  );
  const canViewLeadsOnly = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_USER,
    PERMISSION_KEYS.LEADS,
  );
  const canViewCompaniesAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.COMPANIES,
  );
  const canViewCompaniesOnly = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_USER,
    PERMISSION_KEYS.COMPANIES,
  );
  const canEdit = useCanRolePermissions(ACTIONS.UPDATE, PERMISSION_KEYS.DEALS);
  const canDelete = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.DEALS,
  );

  const { isAdmin } = useAdmin();

  const {
    values,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    errors,
    touched,
    setValues,
    resetForm,
  } = useFormikContext<DEAL_FORM>();

  const [dealOwnerSearch, setDealOwnerSearch] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { isOpen } = useTour();
  const { data: subscription } = useGetSubscription();
  const { setSelectedDealId, selectedDealId, pipelineId } = useDealsContext();
  const { showModal } = useModal();
  const { mutate: deleteDeal, isLoading: deleteLoading } = useDeleteDeal();
  const { data: stages } = useGetStages(pipelineId);
  const { data: accountUsers } = useGetAccountUsers({
    keyword: dealOwnerSearch,
  });

  const { data: fieldsData } = useGetViewDatas({
    type: VIEWDATA_TYPE.INFO_PAGE,
    source: DATA_SOURCE.DEAL,
    'include[0]': 'dataList',
  });

  const customFields = useMemo(() => {
    if (fieldsData?.data?.[0]?.dataList) {
      return fieldsData?.data?.[0]?.dataList.map((dt) => ({
        ...dt,
        value: deal?.data?.find((d) => d.dataId === dt._id)?.value,
      }));
    }

    return [];
  }, [fieldsData, deal]);

  const handleUpdateCustomField = useCallback(
    (dataId: string, value: any, field: Data) => {
      const datas = values?.data ?? [];

      const fieldIndex = datas.findIndex((dt) => dt.dataId === dataId);

      let text = '';
      if (field.type === DATA_TYPE.STRING) {
        //Check min/max length
        if (field.meta.min && value.length && value.length < field.meta.min) {
          text = `Min length of ${field.meta.min} characters required`;
        }
        if (field.meta.max && value.length > field.meta.max) {
          text = `Max length of ${field.meta.max} characters required`;
        }
      }
      if (field.type === DATA_TYPE.NUMBER) {
        //Check min/max length
        if (field.meta.min && !isNil(value) && value < field.meta.min) {
          text = `Min value of ${field.meta.min} required`;
        }
        if (field.meta.max && value > field.meta.max) {
          text = `Max value of ${field.meta.max} required`;
        }
      }

      if (fieldIndex > -1) {
        datas[fieldIndex] = { dataId, value, error: text };
      } else {
        datas.push({ dataId, value, error: text });
      }

      setFieldValue('data', datas);
    },
    [setFieldValue, values?.data],
  );

  useEffect(() => {
    if (isOpen) {
      setValues({
        name: composerDealForm.name,
        pipelineId: composerDealForm.pipelineId._id,
        amount: composerDealForm.amount,
        stageId: composerDealForm.stageId,
        accountUserId: composerDealForm.accountUserId,
        type: composerDealForm.type,
      });
    }
  }, [isOpen, setValues]);

  const { data } = useGetAccountUsers({
    _id: values.accountUserId?.[0] as string,
  });
  const selectedAccountUser = values.accountUserId?.[0]
    ? data?.data[0]
    : undefined;

  const { data: companies } = useGetCompanies({
    enabled: canViewCompaniesAll || canViewCompaniesOnly,
    keyword: companySearch,
  });

  const { data: _selectedCompany } = useGetCompany(
    values.companyId as string,
    { include: ['dataList'] },
    isOpen ? false : true,
  );

  const selectedCompany = !isOpen ? _selectedCompany : mockCompanyData;

  const { data: leads } = useGetLeads({
    enabled: canViewLeadsAll || canViewLeadsOnly,
    keyword: leadSearch,
  });

  const dealOwnerItems = useMemo(
    () =>
      accountUsers?.data.map((lead) => ({
        label: lead.fullName,
        value: lead._id,
      })),
    [accountUsers],
  );

  const contactItems = useMemo(
    () =>
      leads?.data.map((lead) => ({
        photoUrl: lead.photoUrl,
        itemLabel: lead.fullName,
        description: lead.emailAddress,
        value: lead.id,
      })) ?? ([] as AvatarOption[]),
    [leads],
  );

  const companyItems = useMemo(
    () =>
      companies?.data?.map((company) => ({
        itemLabel: company.name,
        value: company.id,
      })),
    [companies],
  );

  const navActionRef = useRef<ElementRef<'button'>>(null);

  const handleDeleteDeal = useCallback(() => {
    deleteDeal(
      { id: selectedDealId, pipelineId },
      {
        onSuccess: () => {
          setSelectedDealId?.(null);
        },
      },
    );
  }, [deleteDeal, pipelineId, selectedDealId, setSelectedDealId]);

  const navOptions = useMemo(
    () =>
      compact([
        canEdit && {
          text: 'Edit',
          key: 'edit',
          onClick: () => {
            setIsEditing(true);
          },
          ['data-tut']: TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DEAL_COMPOSER_EDIT,
        },
        canDelete && {
          text: 'Delete',
          key: 'delete',
          onClick: () => {
            showModal({
              title: 'Deal',
              content: (
                <DeleteItemModal
                  text="deal"
                  onClickDelete={handleDeleteDeal}
                  isLoading={deleteLoading}
                  validation
                />
              ),
            });
          },
          ['data-tut']: TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DELETE,
        },
        {
          onClick: () =>
            showModal({
              content: (
                <CustomFieldsModal
                  source={DATA_SOURCE.DEAL}
                  usedOn={VIEWDATA_TYPE.INFO_PAGE}
                />
              ),
              disableOutsideClick: true,
              resetPadding: true,
              title: 'Custom Fields',
              size: 'xl',
            }),
          key: 'CUSTOM_FIELDS',
          text: 'Custom Fields',
        },
      ]),
    [canEdit, canDelete, deleteLoading, handleDeleteDeal, showModal],
  );

  useEffect(() => {
    setIsEditing(false);
  }, [selectedDealId]);

  useEffect(() => {
    if (!isLoading) setIsEditing(false);
  }, [isLoading]);

  useEffect(() => {
    setFieldValue('stageId', deal?.stageId);
    setFieldValue('closedAt', deal?.closedAt);
  }, [deal, setFieldValue]);

  const filterAccountUserOptions = (inputValue: string) => {
    return dealOwnerItems?.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const loadAccountUserOptions = (
    inputValue: string,
    callback: (options: OptionsOrGroups<unknown, GroupBase<unknown>>[]) => void,
  ) => {
    setTimeout(() => {
      callback(filterAccountUserOptions(inputValue) as any);
    }, 100);
  };

  const filterCompanyOptions = (inputValue: string) => {
    return (
      companyItems?.filter((i) =>
        i.itemLabel.toLowerCase().includes(inputValue.toLowerCase()),
      ) ?? []
    );
  };

  const loadCompanyOptions = (
    inputValue: string,
    callback: (options: AvatarOption[]) => void,
  ) => {
    setTimeout(() => {
      callback(filterCompanyOptions(inputValue));
    }, 100);
  };

  const debouncedSearch = debounce((string, setter) => setter(string), 800);

  const loadLeadOptions = (
    search: string,
    cb: (options: AvatarOption[]) => void,
  ) => {
    debouncedSearch(search, setLeadSearch);

    cb(contactItems);
  };

  const defaultContacts = useMemo(
    () => contactItems?.filter((i) => values.leadId?.includes(i.value)),
    [contactItems, values.leadId],
  );

  if (!deal) {
    return <div />;
  }

  const renderLabel = (
    field: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'> & {
      value: any;
    },
  ) => {
    switch (field?.type) {
      case DATA_TYPE.STRING:
      case DATA_TYPE.MOBILENUMBER:
      case DATA_TYPE.DATE:
        return field.value;
      case DATA_TYPE.BOOLEAN: {
        if (field.value === true) return 'Yes';
        return 'No';
      }
      case DATA_TYPE.NUMBER: {
        switch (field.meta.format as DATA_NUMBER_FORMAT) {
          case DATA_NUMBER_FORMAT.FORMATTED:
            return !isNil(field.value) ? (+field.value).toLocaleString() : null;
          case DATA_NUMBER_FORMAT.UNFORMATTED:
            return field.value;
          case DATA_NUMBER_FORMAT.CURRENCY:
            return usdFormatter.format(field.value);
          case DATA_NUMBER_FORMAT.PERCENTAGE:
            return (+field.value as number).toLocaleString() + '%';
        }
        break;
      }
      case DATA_TYPE.RADIOBOX:
      case DATA_TYPE.SELECT: {
        const faceValue = field.meta.options?.find(
          ({ value }) => field.value === value,
        )?.value;
        return faceValue;
      }
      case DATA_TYPE.MULTISELECT:
      case DATA_TYPE.CHECKBOX: {
        const labels = field.meta.options
          ?.filter((opt) => field.value.includes(opt.value))
          .map((opt) => opt.label)
          .join(', ');
        return labels;
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex flex-col justify-between',
        mode === 'popover'
          ? isAdmin
            ? 'h-[calc(100vh-122px)]'
            : 'h-[calc(100vh-100px)]'
          : 'min-h-[630px]',
      )}
    >
      <div
        data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DEAL_COMPOSER_FORM}
        className={cn(
          'p-[22px]',
          'flex-1',
          'space-y-7',
          mode === 'popover' && 'overflow-y-auto overflow-x-hidden',
        )}
      >
        {isEditing ? (
          <div className="relative">
            <p className="text-sm font-semibold leading-[22px]">
              {(deal?.pipelineId as Pipeline)?.name}
            </p>
            <input
              className="w-full border-b-2 border-secondary text-[22px] font-semibold outline-none"
              value={values.name}
              onChange={(e) => setFieldValue('name', e.target.value)}
            />
            <button
              type="button"
              onClick={() => setFieldValue('name', '')}
              className="absolute right-0 bottom-1.5 hover:text-secondary"
            >
              <MdClose className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold leading-[22px]">
                {(deal?.pipelineId as Pipeline)?.name}
              </p>
              <p className="text-[22px] font-semibold leading-7">
                {deal?.name}
              </p>
            </div>
            {!subscription?.isExpired && !!navOptions.length && (
              <NavAction
                ref={navActionRef}
                label={
                  <TbDotsVertical
                    data-tut={
                      TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.THREE_DOTS_BUTTON
                    }
                    className="h-5 w-5 text-secondary"
                  />
                }
                options={navOptions}
                optionsClassName="w-[120px] right-0"
              />
            )}
          </div>
        )}
        <div className="grid grid-cols-3 items-center gap-y-4">
          <p className="col-span-1 text-sm">Deal Stage</p>
          <div className="col-span-2">
            {isEditing ? (
              <Dropdown
                defaultValue={values.stageId}
                placeholder="Select Stage"
                optionsClassName="w-[220px]"
                selectedClassName="font-semibold bg-secondary-lightest w-[220px]"
                options={
                  stages?.map((stage) => ({
                    label: stage.name,
                    value: stage._id,
                    isSelected: stage._id === values.stageId,
                  })) || []
                }
                size={SIZE.Small}
                onChange={(opt) => setFieldValue('stageId', opt.value)}
              />
            ) : (
              <p
                className={cn(
                  'font-semibold',
                  'rounded-[6px] bg-secondary-lightest px-[13px]',
                  'h-9 w-[220px]',
                  'flex items-center',
                  'text-xs',
                )}
              >
                {stages?.find((stage) => stage._id === deal.stageId)?.name}
              </p>
            )}
          </div>
          <p className="col-span-1 text-sm">Amount</p>
          <div className="col-span-2">
            {isEditing ? (
              <Currency
                className="w-full"
                name="amount"
                onValueChange={(e) => setFieldValue('amount', e)}
                onBlur={handleBlur}
                value={values.amount ?? 0}
                placeholder="Input amount"
                disabled={isSubmitting}
              />
            ) : (
              <p className="text-sm font-semibold text-[#1973E8]">
                $ {convertToAmount(deal.amount)}
              </p>
            )}
          </div>
          <p className="col-span-1 text-sm">Closed Date</p>
          <div className="col-span-2">
            {isEditing ? (
              <DateField
                className="w-full"
                selected={
                  (values.closedAt as Date) && new Date(values.closedAt as Date)
                }
                onChange={(_, value) =>
                  setFieldValue('closedAt', new Date(value as Date))
                }
                placeholder="No Date"
                name="closedAt"
                size={SIZE.Small}
                clearSelected={() => setFieldValue('closedAt', null)}
              />
            ) : (
              <p className="text-sm text-[#1973E8]">
                {deal.closedAt
                  ? dayjs(deal.closedAt).format(DATE_FORMAT_V2)
                  : 'Open deal'}
              </p>
            )}
          </div>
          <p className="col-span-1 text-sm">Tags</p>
          <div className="col-span-2">
            {isEditing ? (
              <TagsField
                defaultValue={values.tagId?.map(
                  (tag) => (tag as unknown as ITag)._id,
                )}
                className="w-full"
                hideLabel
                size={SIZE.Small}
                onChange={(val) =>
                  setFieldValue(
                    'tagId',
                    val.map((tag) => tag.value),
                  )
                }
              />
            ) : (
              <div className="flex flex-1 gap-0.5 flex-wrap">
                {values?.tagId?.map((tag) => (
                  <Tag
                    key={(tag as unknown as ITag)._id}
                    label={(tag as unknown as ITag).tag}
                    size={SIZE.Small}
                  />
                ))}
              </div>
            )}
          </div>
          <p className="col-span-1 text-sm">Deal Type</p>
          <div className="col-span-2">
            {isEditing ? (
              <Select
                className="w-full"
                textType={errors.type && touched.type ? 'danger' : 'default'}
                subText={errors.type && touched.type ? errors.type : undefined}
                label=""
                onChange={(val) => setFieldValue('type', val.value)}
                options={[
                  {
                    value: 'NEW_BUSINESS',
                    label: 'New Business',
                  },
                  {
                    value: 'EXISTING_BUSINESS',
                    label: 'Existing Business',
                  },
                ]}
                size={SIZE.Small}
                name="type"
                placeholder="Select"
                disabled={isSubmitting}
                defaultValue={values.type}
                required
              />
            ) : (
              <p
                className={cn(
                  'text-sm font-semibold leading-8',
                  'border',
                  'h-9 w-fit rounded-[6px] px-2',
                  values.type === 'NEW_BUSINESS'
                    ? 'border-primary-base text-primary-base'
                    : 'border-secondary-base text-secondary-base',
                )}
              >
                {constants.DEAL_TYPES[deal.type]}
              </p>
            )}
          </div>
          <p className="col-span-1 text-sm">Deal Owner</p>
          <div className="col-span-2">
            {isEditing ? (
              <Autocomplete
                className="w-full"
                name="accountUserId"
                size={SIZE.Small}
                state={
                  errors.accountUserId && touched.accountUserId
                    ? 'danger'
                    : undefined
                }
                onChange={(val: any) =>
                  setFieldValue('accountUserId', [val.value])
                }
                defaultValue={
                  selectedAccountUser && [
                    {
                      label: selectedAccountUser?.fullName,
                      value: selectedAccountUser?._id,
                    },
                  ]
                }
                placeholder="Select Deal Owner"
                subText={
                  errors.accountUserId && touched.accountUserId
                    ? errors.accountUserId
                    : ''
                }
                required
                onInputChange={(val) => {
                  setDealOwnerSearch(val);
                }}
                loadOptions={loadAccountUserOptions}
                isDisabled={isSubmitting}
                isClearable={false}
              />
            ) : (
              <div className="flex items-center gap-x-[6px]">
                {deal.accountUserId?.[0] ? (
                  <>
                    <Avatar user={deal?.accountUserId?.[0]} size={SIZE.Small} />
                    <p className="text-sm font-semibold">
                      {(deal.accountUserId as User[])?.[0]?.fullName}
                    </p>
                  </>
                ) : (
                  <p className="text-sm">No Assigned User</p>
                )}
              </div>
            )}
          </div>
          {customFields.map((data) => (
            <Fragment key={data._id}>
              <p className="col-span-1 text-sm">{data.name}</p>
              <div className="col-span-2">
                {isEditing ? (
                  <BaseCustomFieldFormField
                    field={data as any}
                    customData={values?.data}
                    hideLabel
                    handleUpdateCustomField={handleUpdateCustomField}
                    disabled={isSubmitting}
                    size={SIZE.Small}
                  />
                ) : (
                  <p className="text-sm text-[#1973E8]">{renderLabel(data)}</p>
                )}
              </div>
            </Fragment>
          ))}
        </div>
        <div>
          <p className="border-b border-[#DCECF8] py-3.5 text-sm text-content">
            Associated Deal With
          </p>
          <div className="mt-4 grid grid-cols-3 items-center gap-y-4">
            <p className="col-span-1 text-sm">Company</p>
            <div className="col-span-2">
              {isEditing ? (
                <SelectAvatar
                  className="w-full"
                  state={
                    errors.companyId && touched.companyId ? 'danger' : undefined
                  }
                  subText={
                    errors.companyId && touched.companyId
                      ? errors.companyId
                      : ''
                  }
                  defaultValue={
                    selectedCompany && {
                      itemLabel: selectedCompany.name,
                      photoUrl: selectedCompany.photoUrl,
                      value: selectedCompany._id,
                    }
                  }
                  onInputChange={(val) => {
                    setCompanySearch(val);
                  }}
                  loadOptions={loadCompanyOptions}
                  size="md"
                  name="companyId"
                  placeholder="No companies yet"
                  isDisabled={isSubmitting}
                  onClear={() => setFieldValue('companyId', null)}
                  onChange={(val) => {
                    setFieldValue('companyId', (val as AvatarOption)?.value);
                    return val;
                  }}
                />
              ) : (
                <p className="text-sm font-semibold">{deal.companyId?.name}</p>
              )}
            </div>
            <p className="col-span-1 text-sm">Leads</p>
            <div className="col-span-2">
              {isEditing ? (
                <SelectAvatar
                  className="w-full"
                  state={errors.leadId && touched.leadId ? 'danger' : undefined}
                  subText={errors.leadId && touched.leadId ? errors.leadId : ''}
                  loadOptions={loadLeadOptions}
                  size="md"
                  name="leadId"
                  placeholder="No leads yet"
                  isDisabled={isSubmitting}
                  isMulti
                  defaultOptions
                  defaultValue={defaultContacts}
                  onChange={(val) => {
                    setFieldValue(
                      'leadId',
                      (val as AvatarOption[]).map((v) => v.value),
                    );
                  }}
                  onClear={() => setFieldValue('leadId', [])}
                />
              ) : (
                <AvatarGroup size={SIZE.Small} users={deal.leadId} />
              )}
            </div>
          </div>
        </div>
      </div>
      {isEditing && (
        <div
          className={cn(
            'flex justify-end gap-3',
            'p-4 text-right',
            'bg-fill-tertiary',
            'border-t-[0.5px]',
            'border-t-[#DCEDF8]',
          )}
        >
          <Button
            data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DEAL_COMPOSER_SAVE}
            type="submit"
            disabled={isLoading || isSubmitting}
            label="SAVE"
          />
          <Button
            intent={'tertiary'}
            onClick={() => {
              setIsEditing(false);
              resetForm();
            }}
            disabled={isLoading}
            label="CANCEL"
          />
        </div>
      )}
    </form>
  );
};

export default EditDealForm;
